
import { useState, useRef, useEffect, useCallback } from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { cn } from "@/lib/utils";
import haptics from "@/lib/haptics";
import { useChartStore } from "@/stores/chartStore";
import { useSettingsStore } from "@/stores/settingsStore";
import { TIMEFRAMES } from "@/lib/lytick/chart-config";
import { Star, X, MoreHorizontal, Plus } from "lucide-react";

/** Full human-readable label for popover lists */
function timeframeLabel(tf: string): string {
  const preset = TIMEFRAMES.find(t => t === tf);
  if (preset) {
    const m = tf.match(/^(\d+)(m|h|d|w|Mn)$/);
    if (!m) return tf;
    const num = m[1];
    const unit = m[2];
    const unitLabels: Record<string, string> = {
      m: ' Min', h: ' Hour', d: ' Day', w: ' Week', Mn: ' Month',
    };
    return `${num}${unitLabels[unit]?.trim() ?? unit}`;
  }
  return tf.toUpperCase();
}

/** Compact pill label: "1M", "5M", "1H", "4H", "1D" */
function pillLabel(tf: string): string {
  const m = tf.match(/^(\d+)(m|h|d|w|Mn)$/);
  if (!m) return tf.toUpperCase();
  const unitMap: Record<string, string> = { m: 'M', h: 'H', d: 'D', w: 'W', Mn: 'MN' };
  return `${m[1]}${unitMap[m[2]] ?? m[2]}`;
}

export function TimeframeSelector() {
  const { timeframe, setTimeframe } = useChartStore();
  const { hapticsEnabled, favoriteTimeframes, toggleFavoriteTimeframe, addFavoriteTimeframe } = useSettingsStore();
  const [open, setOpen] = useState(false);
  const [customInput, setCustomInput] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  // Keyboard shortcut event: open popover and focus custom input
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<string>).detail;
      if (typeof detail === 'string') {
        setOpen(true);
        setTimeout(() => {
          setCustomInput(detail);
          inputRef.current?.focus();
        }, 50);
      }
    };
    window.addEventListener('lytick:open-timeframe', handler);
    return () => window.removeEventListener('lytick:open-timeframe', handler);
  }, []);

  // Pill click — switch timeframe, no popover interaction
  const handlePillSelect = useCallback((tf: string) => {
    if (hapticsEnabled) haptics.selection();
    setTimeframe(tf);
  }, [hapticsEnabled, setTimeframe]);

  // Popover item click — switch and close
  const handleSelect = useCallback((tf: string) => {
    if (hapticsEnabled) haptics.selection();
    setTimeframe(tf);
    setOpen(false);
  }, [hapticsEnabled, setTimeframe]);

  const handleToggleFav = useCallback((tf: string, e: React.SyntheticEvent) => {
    e.stopPropagation();
    if (hapticsEnabled) haptics.light();
    toggleFavoriteTimeframe(tf);
  }, [hapticsEnabled, toggleFavoriteTimeframe]);

  /**
   * Parse human-readable timeframe input.
   * Accepts: 1m, 5m, 30m, 1h, 2h, 4h, 1d, 1w, 1Mn, 30, 120, etc.
   */
  const parseCustomInput = useCallback((input: string): string | null => {
    const trimmed = input.trim().toLowerCase();
    if (!trimmed) return null;

    if (/^\d+$/.test(trimmed)) {
      const num = parseInt(trimmed, 10);
      if (num <= 0 || num > 525600) return null;
      if (num % 10080 === 0) return `${num / 10080}w`;
      if (num % 43200 === 0) return `${num / 43200}Mn`;
      if (num % 1440 === 0) return `${num / 1440}d`;
      if (num % 60 === 0) return `${num / 60}h`;
      return `${num}m`;
    }

    const match = trimmed.match(/^(\d+)\s*(mn|mo|m|h|d|w)$/i);
    if (!match) return null;

    const num = parseInt(match[1], 10);
    const unit = match[2].toLowerCase();
    if (num <= 0 || num > 12000) return null;

    switch (unit) {
      case "mn":
      case "mo":
        return `${num}Mn`;
      case "w":
        return `${num}w`;
      case "d":
        return `${num}d`;
      case "h":
        return `${num}h`;
      case "m":
        return `${num}m`;
      default:
        return null;
    }
  }, []);

  const handleCustomSubmit = useCallback(() => {
    const tf = parseCustomInput(customInput);
    if (!tf) return;
    if (hapticsEnabled) haptics.selection();
    addFavoriteTimeframe(tf);
    setCustomInput("");
  }, [customInput, hapticsEnabled, addFavoriteTimeframe, parseCustomInput]);

  // Arrow-key navigation across the 4-column all-timeframes grid
  const handleGridKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    const items = Array.from(
      e.currentTarget.querySelectorAll<HTMLButtonElement>('[data-tf-btn]')
    );
    const idx = items.indexOf(document.activeElement as HTMLButtonElement);
    if (idx === -1) return;
    let next = idx;
    if (e.key === 'ArrowRight') next = Math.min(idx + 1, items.length - 1);
    else if (e.key === 'ArrowLeft') next = Math.max(idx - 1, 0);
    else if (e.key === 'ArrowDown') next = Math.min(idx + 4, items.length - 1);
    else if (e.key === 'ArrowUp') next = Math.max(idx - 4, 0);
    else return;
    e.preventDefault();
    items[next].focus();
  }, []);

  const isFav = (tf: string) => favoriteTimeframes.includes(tf);
  const parsedTF = parseCustomInput(customInput);
  const isValidCustom = parsedTF !== null;

  const activePillStyle = {
    background: "var(--lyt-bg-elevated)",
    color: "var(--lyt-text-primary)",
    fontWeight: 600,
    borderRadius: 6,
  };

  const inactivePillStyle = {
    background: "transparent",
    color: "var(--lyt-text-muted)",
  };

  // Popover content — always shows pinned section + full presets grid + custom input
  const popoverContent = (
    <div>
      {/* ── PINNED ── */}
      <div className="px-3 pt-3 pb-2">
        <div className="flex items-center gap-1.5 mb-2">
          <span
            style={{
              fontSize: "var(--text-xs)",
              color: "var(--text-muted)",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
            }}
          >
            Pinned
          </span>
          <span style={{ fontSize: "10px", color: "var(--text-muted)", opacity: 0.5 }}>
            · sorted shortest to longest
          </span>
        </div>

        {favoriteTimeframes.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {favoriteTimeframes.map((tf) => (
              <span
                key={tf}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  background: tf === timeframe
                    ? "color-mix(in srgb, var(--accent-primary) 15%, transparent)"
                    : "var(--bg-surface)",
                  color: tf === timeframe ? "var(--accent-primary)" : "var(--text-secondary)",
                  borderRadius: 6,
                  padding: "3px 6px 3px 8px",
                  fontFamily: "var(--font-mono)",
                  fontSize: "var(--mono-sm)",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                <button
                  type="button"
                  onClick={() => handleSelect(tf)}
                  style={{ background: "none", border: "none", cursor: "pointer", color: "inherit", padding: 0 }}
                >
                  {pillLabel(tf)}
                </button>
                <button
                  type="button"
                  onClick={(e) => handleToggleFav(tf, e)}
                  aria-label={`Unpin ${tf}`}
                  style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", lineHeight: 1, padding: 0, display: "flex", alignItems: "center" }}
                >
                  <X className="size-2.5" />
                </button>
              </span>
            ))}
          </div>
        ) : (
          <p style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)", opacity: 0.5, paddingTop: 2 }}>
            No pins — star timeframes below
          </p>
        )}
      </div>

      {/* ── ALL PRESETS ── */}
      <div style={{ height: 1, background: "var(--lyt-border-subtle)", margin: "0 12px 10px" }} />
      <div className="px-3 pb-2">
        <p style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>
          All timeframes
        </p>
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
        <div className="grid grid-cols-4 gap-1" onKeyDown={handleGridKeyDown}>
          {TIMEFRAMES.map((tf, i) => {
            const pinned = favoriteTimeframes.includes(tf);
            const active = tf === timeframe;
            return (
              <button
                key={tf}
                type="button"
                data-tf-btn
                tabIndex={i === 0 ? 0 : -1}
                onClick={() => handleSelect(tf)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSelect(tf);
                  }
                }}
                className="hover:bg-surface-hover"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 3,
                  padding: "6px 4px",
                  borderRadius: 8,
                  background: active
                    ? "color-mix(in srgb, var(--accent-primary) 12%, transparent)"
                    : "var(--bg-surface)",
                  border: "none",
                  cursor: "pointer",
                  color: active ? "var(--accent-primary)" : "var(--text-secondary)",
                  fontFamily: "var(--font-mono)",
                  fontSize: "var(--mono-sm)",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {pillLabel(tf)}
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => handleToggleFav(tf, e)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleToggleFav(tf, e); } }}
                  aria-label={pinned ? `Unpin ${tf}` : `Pin ${tf}`}
                  style={{ display: "flex", alignItems: "center", lineHeight: 1 }}
                >
                  <Star
                    className="size-2"
                    style={{ color: pinned ? "var(--accent-amber-gold)" : "var(--text-muted)" }}
                    fill={pinned ? "var(--accent-amber-gold)" : "none"}
                  />
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── CUSTOM INPUT ── */}
      <div style={{ height: 1, background: "var(--lyt-border-subtle)", margin: "0 12px 10px" }} />
      <div className="px-3 pb-3">
        <div className="flex items-center gap-1.5">
          <div className="relative flex-1">
            <Plus className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3" style={{ color: "var(--text-muted)", opacity: 0.5 }} />
            <input
              ref={inputRef}
              data-timeframe-input
              type="text"
              inputMode="text"
              autoComplete="off"
              placeholder="e.g. 30m, 2h, 1d"
              value={customInput}
              onChange={(e) => setCustomInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleCustomSubmit(); }}
              style={{
                width: "100%",
                height: 34,
                background: "var(--bg-surface)",
                border: "1px solid var(--lyt-border-subtle)",
                borderRadius: 6,
                color: "var(--text-primary)",
                fontSize: "var(--text-sm)",
                fontFamily: "var(--font-mono)",
                paddingLeft: 28,
                paddingRight: 8,
                outline: "none",
              }}
            />
          </div>
          <button
            type="button"
            onClick={handleCustomSubmit}
            disabled={!isValidCustom}
            style={{
              height: 34,
              padding: "0 12px",
              borderRadius: 6,
              border: "none",
              fontSize: "var(--text-xs)",
              fontWeight: 600,
              cursor: isValidCustom ? "pointer" : "not-allowed",
              background: isValidCustom ? "var(--accent-primary)" : "var(--bg-surface)",
              color: isValidCustom ? "#000" : "var(--text-muted)",
              opacity: isValidCustom ? 1 : 0.45,
              transition: "opacity 0.15s",
            }}
          >
            Add
          </button>
        </div>
      </div>
    </div>
  );

  const displayTimeframes = favoriteTimeframes.includes(timeframe)
    ? favoriteTimeframes
    : [...favoriteTimeframes, timeframe];

  return (
    <div className="relative flex items-center gap-0.5">
      {/* ── VISIBLE PILLS ── */}
      {displayTimeframes.map((tf) => {
        const active = tf === timeframe;
        return (
          <button
            key={tf}
            type="button"
            onClick={() => handlePillSelect(tf)}
            aria-label={`${tf} timeframe`}
            aria-pressed={active}
            className="hover:bg-surface-hover"
            style={{
              ...(active ? activePillStyle : inactivePillStyle),
              fontFamily: "var(--font-mono)",
              fontSize: "var(--mono-sm)",
              fontVariantNumeric: "tabular-nums",
              letterSpacing: "0.02em",
              borderTop: "none",
              borderLeft: "none",
              borderRight: "none",
              borderBottom: active ? "2px solid var(--lyt-accent)" : "none",
              padding: "0 10px",
              height: 28,
              cursor: "pointer",
              transition: "color 0.12s, border-color 0.12s, background 0.12s",
            }}
          >
            {pillLabel(tf)}
          </button>
        );
      })}

      {/* ── ⋯ MORE BUTTON (Radix Popover trigger) ── */}
      <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
        <PopoverPrimitive.Trigger asChild>
          <button
            type="button"
            aria-label="More timeframes"
            className="hover:bg-surface-hover"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: open ? "var(--lyt-text-secondary)" : "var(--lyt-text-muted)",
              background: open ? "var(--lyt-bg-elevated)" : "transparent",
              border: "none",
              borderRadius: 6,
              padding: "0 8px",
              height: 28,
              cursor: "pointer",
              transition: "color 0.1s, background 0.1s",
            }}
          >
            <MoreHorizontal className="size-3.5" />
          </button>
        </PopoverPrimitive.Trigger>
        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Content
            align="end"
            side="bottom"
            sideOffset={6}
            avoidCollisions
            style={{
              width: 280,
              backgroundColor: "var(--bg-elevated)",
              border: "1px solid var(--lyt-border-subtle)",
              borderRadius: 12,
              boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
              zIndex: 50,
              overflow: "hidden",
              animationDuration: "150ms",
            }}
            className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-1"
          >
            {popoverContent}
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>
    </div>
  );
}
